import { Helmet } from 'react-helmet-async';

const defaultSEO = {
  siteName: 'SYCE',
  siteUrl: 'https://syce.me',
  defaultTitle: 'SYCE - Music & Video Distribution | Up to 100% Royalties',
  defaultDescription: 'Distribute your music to Spotify, Apple Music, TikTok & 150+ platforms. Keep up to 100% of your royalties. Start free or upgrade for more features.',
  defaultImage: 'https://syce.me/og-image.png',
  twitterHandle: '@sloydistro'
};

export default function SEO({ 
  title, 
  description, 
  image, 
  url,
  type = 'website',
  article = null,
  noIndex = false
}) {
  const seo = {
    title: title ? `${title} | SYCE` : defaultSEO.defaultTitle,
    description: description || defaultSEO.defaultDescription,
    image: image || defaultSEO.defaultImage,
    url: url ? `${defaultSEO.siteUrl}${url}` : defaultSEO.siteUrl
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <link rel="canonical" href={seo.url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:site_name" content={defaultSEO.siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:site" content={defaultSEO.twitterHandle} />

      {/* Article specific (for blog posts) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author} />
          {article.tags?.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}
    </Helmet>
  );
}

// Pre-defined SEO configurations for common pages
export const pageSEO = {
  home: {
    title: null, // Uses default
    description: 'Distribute music to Spotify, Apple Music, TikTok & 150+ platforms. Keep up to 100% royalties. Start free today.',
    url: '/'
  },
  distribution: {
    title: 'Music & Video Distribution to 150+ Platforms | SYCE',
    description: 'Distribute music to Spotify, Apple Music, YouTube, TikTok & 150+ platforms. Pro plans keep 100% royalties.',
    url: '/distribution'
  },
  podcastHosting: {
    title: 'Podcast Hosting & Distribution | SYCE',
    description: 'Professional podcast hosting with distribution to Apple Podcasts, Spotify & 10+ directories. Free 30-day trial.',
    url: '/podcast-hosting'
  },
  pricing: {
    title: 'Pricing Plans | SYCE Music Distribution',
    description: 'Music distribution starting free. Starter & Essential: 90% royalties. Pro & Select: 100% royalties. Mastering included 1st year.',
    url: '/pricing'
  },
  sync: {
    title: 'Sync Licensing Opportunities | SYCE',
    description: 'Get your music in TV, film & ads. Browse real sync opportunities from verified music supervisors.',
    url: '/sync'
  },
  audioServices: {
    title: 'Mixing & Mastering Services | SYCE',
    description: 'Professional mixing & mastering from Grammy-nominated engineers. Affordable rates for independent artists.',
    url: '/audio-services'
  },
  blog: {
    title: 'Music Industry Blog | SYCE',
    description: 'Tips & strategies for independent artists. Music marketing, royalties, playlist pitching & more.',
    url: '/blog'
  },
  contact: {
    title: 'Contact Us | SYCE',
    description: 'Get support for music distribution, podcast hosting, sync licensing & more. Fast response guaranteed.',
    url: '/contact'
  },
  login: {
    title: 'Sign In | SYCE',
    description: 'Sign in to your SYCE account. Access releases, analytics, earnings & marketing tools.',
    url: '/login',
    noIndex: true
  },
  register: {
    title: 'Create Account | SYCE',
    description: 'Join SYCE and start distributing music to 150+ platforms. Start free with 90% royalties or upgrade for 100%.',
    url: '/register'
  },
  partnerLogin: {
    title: 'Partner Portal | SYCE',
    description: 'SYCE Partner Portal for music supervisors & agencies. Post sync opportunities & discover artists.',
    url: '/partners/login'
  },
  partnerRegister: {
    title: 'Become a Partner | SYCE',
    description: 'Register as a SYCE sync licensing partner. Access independent artists for TV, film & ads.',
    url: '/partners/register'
  },
  merch: {
    title: 'Official Merch | SYCE',
    description: 'Shop official SYCE merchandise. Premium apparel for independent artists. Black-owned brand.',
    url: '/merch'
  },
  beats: {
    title: 'Beat Store | SYCE',
    description: 'Browse beats from independent producers. Exclusive & non-exclusive licenses available.',
    url: '/beats',
    noIndex: true
  },
  publishing: {
    title: 'Music Publishing | SYCE',
    description: 'Collect all your royalties worldwide. Performance, mechanical & sync. Keep 100% ownership.',
    url: '/publishing'
  },
  forgotPassword: {
    title: 'Reset Password | SYCE',
    description: 'Reset your SYCE account password. Secure and easy account recovery.',
    url: '/forgot-password',
    noIndex: true
  }
};
